import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import BaseRouter from 'scoreboard/components/base-router'
import appDisplay from 'scoreboard/components/app-display'
import Dataset from './model/dataset'
import DatasetsOverview from './overview/datasets-overview'
import DatasetsRecords from './records/datasets-records'
import DatasetsEdit from './edit/datasets-edit'
import DatasetsNav from 'scoreboard/datasets/datasets-nav'
import FieldList from './overview/field-list'
import datasetRequests from './dataset-requests'
import DatasetNotReady from './not-available/dataset-not-ready'
import viewEditTabs from 'scoreboard/components/view-edit-tabs.hbs'
import DatasetsFolderOverview from './overview/datasets-folder-overview'
import conductor from 'scoreboard/components/conductor'
import globals from 'scoreboard/components/globals'
import DatasetsEditViewOrFolder from './edit/datasets-edit-view-or-folder'
import layoutManager from 'scoreboard/components/layout-manager'
import JsonStreamer from 'scoreboard/components/json-streamer'
import streamLoader from 'scoreboard/components/export/stream-loader'
import constants from 'scoreboard/components/constants'
import exportState from 'scoreboard/components/export/export-state'
import spectator from 'scoreboard/components/spectator'
import DatasetsCreate from './edit/datasets-create'
import wrapModel from 'scoreboard/components/wrap-model'
import errorHandler from 'scoreboard/components/error-handler'

/**
 * @extends BaseRouter
 */
var DatasetsRouter = BaseRouter.extend(/** @lends DatasetsRouter.prototype */{

	routes: {
		'organizations/:orgId/datasets/new(/)': 'createFirst',
		'datasets/new(/)': 'create',
		'datasets/export(/)': 'exportUnsaved',
		'datasets/records(/)': 'exportUnsavedRecords',
		// Parameterized routes must go after export routes
		'datasets/:id(/)': 'overview',
		'datasets/:id/fields(/)': 'fields',
		'datasets/:id/records(/)': 'records',
		'datasets/:id/edit(/)': 'edit',
		'datasets/:id/new(/)': 'create',
		'datasets/:id/new-folder(/)': 'createFolder'
	},

	Nav: DatasetsNav,
	Model: Dataset,
	section: 'datasets',

	initialize: function () {
		BaseRouter.prototype.initialize.apply(this, arguments)

		this.listenTo(Backbone, Dataset.DRILLDOWN, this._drilldown.bind(this))
	},

	nav: function(options) {
		// If we're attempting to draw the nav from a drilldown action
		// we're going to want to render the datasets nav like normal.
		// https://github.com/SpiderStrategies/Scoreboard/issues/30968 Highlights
		// a special case where spectated dataset widgets weren't updating their
		// navs properly. We're blowing away spectator.getCurrent() here because
		// drilling down takes the user out of spectating mode, and the nav will
		// update properly once that's established.
		if (this.hasDrilldown) {
			spectator.setCurrent()
			delete this.hasDrilldown
		}

		return BaseRouter.prototype.nav.apply(this, arguments)
	},

	// don't guard when switching between the overview and records tabs
	preventGuard: function(href) {
		// both URLs are in datasets
		return conductor.section(href) == conductor.section() &&
			// both URLs refer to the same dataset
			conductor.id(href) == conductor.id(href) &&
			// neither route is for the edit tab
			!_.endsWith(href, '/edit') &&
			!_.endsWith(Backbone.history.fragment, '/edit') &&
			// we aren't on the new folder screen
			!_.endsWith(Backbone.history.fragment, '/new-folder')
	},

	// #34339 override overviewWithModel to ensure correct display
	overviewWithModel: function(model) {
		return this.overview(model.id)
	},

	/**
	 * @param id dataset id
	 * @param {Object} opt.request request attributes
	 */
	overview: function(id, opt) {
		if (this.handleNoCalendars()) {
			return
		}
		if (this.handleLicenseRestriction({section: 'datasets'})) {
			return
		}

		opt = opt || {}

		let chartDrilldown = !!opt.chartDesigner
		if (chartDrilldown) {
			// If we're doing a chart drilldown, we want to start with a fresh
			// request
			datasetRequests.remove(id)
		}

		var request = datasetRequests.get(id)
		request.records = false
		request.showTables = true
		// #30105 needed for both snapshot and non-snapshot metrics
		request.calendarPeriodId = globals.periodId
		// the server will only send it if it makes sense
		request.others = true
		request.showDashboardTables = false
		// needed for the total display
		request.total = true
		// #43245 This needs to be set explicitly
		request.includeDataset = true

		if (chartDrilldown) {
			request.chartDesigner = opt.chartDesigner
			request.chartDesignerSerie = opt.serie
			request.filters = opt.filters
		}

		_.extend(request, opt.request)
		const xhr = $.ajax({
			url: 'api/datasets/' + id + '/query',
			type: 'post',
			data: JSON.stringify(request),
			error: response => {
				if (!this._maybeDisplayNotReadyView('overview', response)) {
					errorHandler(response)
				}
			}
		})

		// We're not transitioning, let the app-display build the view
		appDisplay.display(xhr, response => {
			const model = new Dataset(response.dataset)

			if (response.dataset.type === 'folder') {
				this.applyModel(model)
				return new DatasetsFolderOverview({
					model,
					tabs: viewEditTabs,
					overviewTabCode: 'common.view'
				})
			} else {
				// If this was a chart drilldown, we want the overview to work with
				// the request that was used in the actual query, not what we
				// originally sent to the server
				let requestForView = chartDrilldown ? response.chartQueryRequest : request

				var view = this._queryView(DatasetsOverview, 'overview', requestForView, response, opt)
				this.openingTransition = false
				return view
			}
		}, opt.delayViewRemove == true)

		const modelPromise = xhr.then(response => {
			if (_.isNil(response) || _.isNil(response.dataset)) return Promise.reject()

			return new Dataset(response.dataset)
		})
		return this.navForModel({
			id,
			params: {
				ensureViewable: true
			}
		}, modelPromise)
	},

	exportUnsaved: function() {
		const request = exportState.state
		request.includeDataset = true
		const xhr = $.post({
			url: 'api/datasets/' + request.id + '/query',
			data: JSON.stringify(request)
		})
		appDisplay.display(xhr, response => {
			const view = this._queryView(DatasetsOverview, 'overview', request, response, {})
			this.openingTransition = false
			return view
		})
	},

	/**
	 * Allows direct drilldown to the dataset overview using the provided
	 * dataset request settings (filters, aggregationType, etc)
	 *
	 * @param {Number} id the dataset id
	 * @param {Object} [opt.request] (e.g. series.datasetRequest(), see data-table-view.js)
	 */
	_drilldown: function (id, opt = {}) {
		const route = 'datasets/' + id
		if (this.handleLicenseRestriction({section: 'datasets'})) {
			// #55157 navigate to the drilldown route so the url is updated
			// and the user is in the correct section when displaying
			// the license restriction page
			conductor.navigate(route)
			return
		}
		if (layoutManager.sharedResource) {
			conductor.navigate(route)
		} else {
			this.hasDrilldown = true
			// Lets the overview/header know it's being called because of a drilldown action
			opt.drilldown = this.hasDrilldown
			appDisplay.setDrilldown()
			this.overview(id, opt).then(() => {
				conductor.navigate(route, {trigger: false})
				Backbone.trigger(`${Dataset.DRILLDOWN}:done`, this, 'overview', id)
			})
		}
	},

	/**
	 * Users will see fields in a dialog window, but when they are exported
	 * to PDF/PPT they must be rendered full screen in 'readOnly' mode.
	 */
	fields: function(id) {
		Promise.all([
			$.get(`api/datasets/${id}?fields=true`),
			$.get(`api/datasets/${id}/field-examples`)
		]).then(results => {
			const [dataset, examples] = results
			appDisplay.display(new FieldList({
				model: new Dataset(dataset),
				filters: {},
				examples,
				readOnly: true
			}))
		})

	},

	records: function(id) {
		if (this.handleNoCalendars()) {
			return
		}
		if (this.handleLicenseRestriction({section: 'datasets'})) {
			return
		}

		var request = datasetRequests.get(id)
		request.records = true
		request.showTables = false
		request.calendarPeriodId = globals.periodId
		request.total = true
		// #43245 This needs to be set explicitly
		request.includeDataset = true
		// #50977 needs to be set when total display is for a VLF
		request.showNumResults = true

		const modelPromise = this._streamRecords(request, id).then(view => view.model)

		this.navForModel({ id }, modelPromise)
		this.openingTransition = false
	},

	exportUnsavedRecords: function() {
		const request = exportState.state
		this._streamRecords(request, request.id)
	},

	/**
	 * @returns {Promise<View>}
	 */
	_streamRecords: function (request, id) {
		var streamer = new JsonStreamer()
		const def = $.Deferred()

		// Get all the properties for the dataset before the records are
		// streamed
		const response = {}
		this.listenTo(streamer, 'data', (propertyName, value) => {
			response[propertyName] = value
		})

		// Display all records if this is an export
		const loadAllConditionally = (view, stream) => {
			if (layoutManager.inLayout(constants.LAYOUT_EXPORT)) {
				streamLoader(view, stream)
			}
		}

		// Records have started
		this.listenToOnce(streamer, 'stream', stream => {
			let view
			stream.once('data', d => {
				view = this._queryView(DatasetsRecords,
					'records',
					request,
					response,
					{initialData: d})
				_.invoke(view, 'setupStream', stream)
				loadAllConditionally(view, stream)
				def.resolve(view)
			})
			stream.once('end', () => {
				if (!view) {
					// Stream ended with no results
					view = this._queryView(DatasetsRecords,
						'records',
						request,
						response)
					loadAllConditionally(view, stream)
					def.resolve(view)
				}
			})
		})
		appDisplay.display(def, view => {
			view.on('remove', () => streamer.abort())
			return view
		})

		streamer.start({
			stream: 'records',
			method: 'POST',
			url: 'api/datasets/' + id + '/query',
			data: JSON.stringify(request),
			error: (err) => this.handleError(err)
		})

		return def
	},

	createFolder: function(relativeId, orgId) {
		if (this.handleLicenseRestriction({ section: 'datasets' })) {
			return
		}
		orgId = orgId || globals.orgId
		var folder = new Dataset({
			organizationId: orgId,
			relativeId: relativeId,
			type: 'folder',
			icon: 'folder'
		})

		const viewCreator = relative => {
			let datasetWithPermissions = relative.type === 'view' ?
				relative.rootDataset : relative
			let permissions = _.pick(datasetWithPermissions, 'administer',
				'modifyPermissions', 'modifyViews', 'modifyRecords')
			folder.set(permissions)
			return new DatasetsEditViewOrFolder({
				relative: new Dataset(relative),
				model: folder,
				tabs: viewEditTabs,
				overviewTabCode: 'common.view'
			})
		}
		this.navForNewModel({
			relativeId,
			orgId,
			// Normally a new model doesn't require an XHR first, so we can just
			// leverage the ability to pass a promise/viewCreator here
			form: $.get('api/datasets/' + relativeId + '?permissions=true'),
			viewCreator,
			model: folder,
			delayViewRemove: true
		})
	},

	createFirst: function (orgId) {
		this.create(null, orgId)
	},

	create: function (relativeId, orgId) {
		if (this.handleLicenseRestriction({section: 'datasets'})) {
			return
		}
		$.when(this._resolveOrg(relativeId, orgId)).done(parentId => {
			let organizationId = orgId || globals.orgId
			let model = new Dataset({
				relativeId,
				organizationId,
				type: 'dataset',
				icon: 'dataset',
				// start off the dataset with standard permissions
				permissions: []
			})

			const navPromise = this.nav({ id: parentId, orgId: organizationId, toggleEditViewState: true })
			let dataset = new DatasetsCreate({ model })
			appDisplay.display(dataset)
			navPromise.then(() => this.addTransient(dataset, parentId, model))
		})
	},

	edit: function(id) {
		if (this.handleNoCalendars()) {
			return
		}
		if (this.handleLicenseRestriction({section: 'datasets'})) {
			return
		}

		var opts = {
			connections: true,
			fields: true,
			grandchildren: false,
			permissions: true,
			// so that we can display "Dataset link to {0}"
			// https://github.com/SpiderStrategies/Scoreboard/issues/16390#issuecomment-384765442
			linkedDatasets: true,
			treeItems: true, // needed for rendering security filters
			synthesizeFilters: false
		}

		const modelPromise = this.load(id, opts)
		appDisplay.display(modelPromise, model => {
			if (model.get('type') === Dataset.DATASET) {
				return new DatasetsEdit({ model })
			} else {
				return new DatasetsEditViewOrFolder({ model })
			}
		})
		this.navForModel({id, edit: true}, modelPromise)
		this.openingTransition = false
	},

	/**
	 * Handles views that are based on dataset queries.  If the view is ready we can
	 * display that, but if it's not ready we'll display a different view.
	 */
	_queryView: function(ReadyView, subsection, request, response, options) {
		// if there isn't at least one field and one connection defined
		const dataset = new Dataset(response.dataset)
		this.applyModel(dataset)
		// only the explore and records tabs show the dataset loading animation
		const isViewWithNotReady = subsection === 'overview' || subsection === 'records'
		const hasQueryError = !_.isEmpty(response.queryError)
		// #32080 if for some reason we're in a state where the dataset is
		// READY or INDEXED, but there is a queryError in the response
		// prevent an infinite loop from the records tab JsonStreamer calling
		// this method and just display the queryError
		const isComplete = dataset.isComplete()
		const completeWithError =  isComplete && hasQueryError
		const showReadyView = (dataset.isDisplayable() && !hasQueryError) || completeWithError
		const showNotReadyView = isViewWithNotReady && (hasQueryError || !isComplete || !dataset.isDisplayable())
		if (!showReadyView && showNotReadyView) {
			return this._getNotReadyView(dataset, subsection, response)
		}

		if (showReadyView) {
			this._getReadyView(ReadyView, dataset, request, response, options)
		}

		// catch all: if we reach here, then just do a basic check of whether the dataset is displayable
		if (dataset.isDisplayable()) {
			return this._getReadyView(ReadyView, dataset, request, response, options)
		}

		return this._getNotReadyView(dataset, subsection, response)
	},

	_getReadyView: function(ReadyView, dataset, request, response, options) {
		delete this.notReadyView

		return new ReadyView(_.defaults({
			model: dataset,
			request: request,
			response: response,
			openingTransition: this.openingTransition
		}, options))
	},

	_getNotReadyView: function(dataset, subsection, response, forceNotReadyView = false) {
		const isQuickset = !!this.notReadyView
		_.invoke(this.notReadyView, 'remove')

		this.notReadyView = new DatasetNotReady({
			model: dataset,
			subsection,
			// If we're going directly to (via clicking on a nav item) a dataset that's not ready, do not
			// perform the opening animation fade
			isQuickset,
			queryError: response.queryError,
			forceNotReadyView
		}).on('ready', param => {
			if (subsection == 'overview') {
				this.openingTransition = true
				this.overview(param.dataset.id, { delayViewRemove: true })
			} else if (subsection == 'records') {
				this.records(param.dataset.id)
			} else {
				this.edit(param.dataset.id)
			}
		})

		return this.notReadyView
	},

	/**
	 *
	 * @param subsection {'records' | 'overview' | 'edit'}
	 * @returns {boolean} true if the not ready view was displayed
	 * @private
	 */
	_maybeDisplayNotReadyView: function(subsection, xhr) {
		const responseJSON = _.get(xhr, 'responseJSON', {})
		const dataset = responseJSON.dataset
		const hasDataset = !_.isNil(dataset)
		// don't want to display this if we didn't get the dataset's metadata back
		if (!hasDataset) return false

		// display the loading screen if we hit an error during the query or
		// if the dataset is marked as not queryable
		const hasQueryError = !_.isNil(responseJSON.queryError)
		const queryable = _.get(dataset, 'queryable', true)
		if (hasQueryError || !queryable) {
			const datasetModel = wrapModel(responseJSON.dataset, Dataset)
			const view = this._getNotReadyView(datasetModel, subsection, xhr, true)
			appDisplay.display(view)
			return true
		}

		return false
	},

	/**
	 * @returns {boolean} true if dataset-not-ready.js was displayed, false otherwise
	 */
	handleError: function(err) {
		const notReadyViewDisplayed = this._maybeDisplayNotReadyView('records', err.xhr)
		if (notReadyViewDisplayed) return true

		errorHandler(err.xhr)
		return false
	}
})

export default DatasetsRouter

DatasetsRouter.__filename__ = 'datasets/datasets-router.js'
